import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { DeviceDiscoveryAnimation } from '../animations/device-discovery-animation';
import { useToast } from '../../hooks/use-toast';
import { Radar, Layers, Info } from 'lucide-react';
import { cn } from '../../lib/utils';

interface DeviceDiscoverySimulatorProps {
  onDevicesFound?: (devices: any[]) => void;
  className?: string;
}

export function DeviceDiscoverySimulator({ onDevicesFound, className }: DeviceDiscoverySimulatorProps) {
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const { toast } = useToast();

  const handleStartDiscovery = () => {
    setIsDiscovering(true);
    setIsComplete(false);
    
    // Simulate a discovery process that takes 10 seconds
    setTimeout(() => {
      setIsDiscovering(false);
      setIsComplete(true);
      
      // Generate some sample devices
      const discoveredDevices = [
        {
          name: "Router-XYZ",
          ipAddress: "192.168.1.1",
          type: "Network Router",
          group: "Network",
          status: "up",
          performance: 75,
        },
        {
          name: "WebServer-Alpha",
          ipAddress: "192.168.1.10",
          type: "Web Server",
          group: "Web",
          status: "up",
          performance: 92,
        },
        {
          name: "Database-Primary",
          ipAddress: "192.168.1.15",
          type: "Database Server",
          group: "Database",
          status: "up",
          performance: 88,
        },
        {
          name: "Firewall-Edge",
          ipAddress: "192.168.1.254",
          type: "Firewall",
          group: "Network",
          status: "up",
          performance: 95,
        },
      ];
      
      if (onDevicesFound) {
        onDevicesFound(discoveredDevices);
      }
      
      toast({
        title: "Discovery Complete",
        description: `Found ${discoveredDevices.length} devices on the network.`,
        variant: "default",
      });
    }, 10000);
  };

  return (
    <Card className={cn(className, "bg-white")}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Radar className="h-5 w-5" />
          Network Device Discovery
        </CardTitle>
        <CardDescription>
          Scan your network to automatically discover and add devices to your inventory
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isDiscovering ? (
          <DeviceDiscoveryAnimation 
            isLoading={isDiscovering} 
            onComplete={() => setIsComplete(true)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center p-6 space-y-6 text-center">
            {isComplete ? (
              <div className="flex flex-col items-center space-y-3">
                <div className="h-20 w-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                  <Layers className="h-10 w-10 text-green-600 dark:text-green-300" />
                </div>
                <h3 className="text-xl font-semibold">Discovery Complete</h3>
                <p className="text-muted-foreground max-w-md">
                  All devices have been discovered and added to your inventory. You can now manage and monitor these devices.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-3">
                <div className="h-20 w-20 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Info className="h-10 w-10 text-blue-600 dark:text-blue-300" />
                </div>
                <h3 className="text-xl font-semibold">Ready to Discover</h3>
                <p className="text-muted-foreground max-w-md">
                  Click the button below to start scanning your network for devices. This process will automatically discover and add devices to your inventory.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => {
            setIsDiscovering(false);
            setIsComplete(false);
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleStartDiscovery} 
          disabled={isDiscovering}
        >
          {isDiscovering ? "Discovering..." : isComplete ? "Discover Again" : "Start Discovery"}
        </Button>
      </CardFooter>
    </Card>
  );
}